import { useSearch } from "@redotech/react-util/search";
import { t, type Translate, useTranslation } from "@redotech/redo-i18n";
import RedoIconArrowLeft from "@redotech/redo-icons/arrow-left.svg";
import SearchSm from "@redotech/redo-icons/search-sm.svg";
import SettingsIcon from "@redotech/redo-icons/settings-02.svg";
import classNames from "classnames";
import Fuse from "fuse.js";
import {
  JSXElementConstructor,
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { RedoIcon } from "../arbiter-components/icon/redo-icon";
import { RedoTextInput } from "../arbiter-components/input/redo-text-input";
import { CommandPaletteOpenContext } from "../arbiter-components/navigation/command-palette-open-context";
import * as navigationCss from "../arbiter-components/navigation/redo-navigation.module.css";
import {
  RedoSecondaryNavItem,
  SecondaryNavItemProps,
  useNavTreeNode,
} from "../arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { Flex } from "../flex";
import { Text } from "../text";
import { dispatchTextHighlight } from "../text-highlighter-provider";
import * as darkModeCss from "../theme/component-colors-dark.module.css";
import { UserProfile } from "./common/redo-nav-user-profile";

export interface SettingItem {
  href: string;
  friendlyName: string;
  id: string;
  searchHints?: readonly string[];
  searchMatches?: readonly SettingItemSearchMatch[];
  searchGroups?: readonly SettingItemSearchMatch[];
  /** The page's top tabs, in page order, when its search matches name one. */
  searchTabs?: readonly SettingItemSearchTab[];
  searchTerms?: readonly string[];
  UnfilledIcon?: JSXElementConstructor<any>;
  FilledIcon?: JSXElementConstructor<any>;
  /** Optional nested sub-items, rendered as an expandable folder. */
  children?: SettingItem[];
}

export interface SettingItemSearchMatch {
  /** Focused destination for a setting or group search result. */
  href?: string;
  label: string;
  contextLabel?: string;
  /** The catalog group the match sits in; a page shows one match per group. */
  groupId?: string;
  searchTerms?: readonly string[];
  settingId?: string;
  /** One of the page's `searchTabs`, when the match sits on a top tab. */
  tabId?: string;
}

export interface SettingItemSearchTab {
  /** Opens the page on this tab; makes the tab's search heading clickable. */
  href?: string;
  id: string;
  label: string;
}

export enum SettingsSectionEnum {
  GENERAL = "general",
  PERSONAL = "personal",
  AI = "ai",
  // CATALOG = "catalog",
  CONVERSION = "conversion",
  CHARGEBACKS = "chargebacks",
  CUSTOMER_ACCOUNTS = "customer-accounts",
  MARKETING = "marketing",
  ORDER_TRACKING = "order-tracking",
  PAYMENTS = "payments",
  RETURNS_AND_CLAIMS = "returns-and-claims",
  PRODUCTS = "products",
  WARRANTIES = "warranties",
  INVENTORY_MANAGEMENT = "inventory-management",
  ORDER_FULFILLMENT = "order-fulfillment",
  WAREHOUSE = "warehouse",
  DOCUMENTS_AND_PRINTING = "documents-and-printing",
  SHIPPING = "shipping",
  INTERNATIONAL = "international",
  SUPPORT = "support",
  RECOVER = "recover",
  // Redo-internal: supplies selling configuration, gated to the supplies team.
  SUPPLIES = "supplies",
}

export const settingsSectionToFriendly: Record<SettingsSectionEnum, string> = {
  [SettingsSectionEnum.GENERAL]: "General",
  [SettingsSectionEnum.PERSONAL]: "Personal",
  [SettingsSectionEnum.AI]: "AI",
  // [SettingsSectionEnum.CATALOG]: "Catalog",
  [SettingsSectionEnum.CONVERSION]: "Conversion",
  [SettingsSectionEnum.CHARGEBACKS]: "Chargebacks",
  [SettingsSectionEnum.CUSTOMER_ACCOUNTS]: "Customer accounts",
  [SettingsSectionEnum.MARKETING]: "Marketing",
  [SettingsSectionEnum.ORDER_TRACKING]: "Order tracking",
  [SettingsSectionEnum.PAYMENTS]: "Payments",
  [SettingsSectionEnum.RETURNS_AND_CLAIMS]: "Returns & claims",
  [SettingsSectionEnum.PRODUCTS]: "Products",
  [SettingsSectionEnum.WARRANTIES]: "Warranties",
  [SettingsSectionEnum.INVENTORY_MANAGEMENT]: "Inventory management",
  [SettingsSectionEnum.ORDER_FULFILLMENT]: "Order Management",
  [SettingsSectionEnum.WAREHOUSE]: "Warehouse operations",
  [SettingsSectionEnum.DOCUMENTS_AND_PRINTING]: "Documents & printing",
  [SettingsSectionEnum.SHIPPING]: "Shipping",
  [SettingsSectionEnum.INTERNATIONAL]: "International",
  [SettingsSectionEnum.SUPPORT]: "Support",
  [SettingsSectionEnum.RECOVER]: "Recover",
  [SettingsSectionEnum.SUPPLIES]: "Supplies",
};

export function localizedSettingsSectionNames(
  t: Translate,
): Record<SettingsSectionEnum, string> {
  return {
    [SettingsSectionEnum.GENERAL]: t("General", { context: "settings nav" }),
    [SettingsSectionEnum.PERSONAL]: t("Personal", { context: "settings nav" }),
    [SettingsSectionEnum.AI]: t("AI", { context: "artificial intelligence" }),
    [SettingsSectionEnum.CONVERSION]: t("Conversion", {
      context: "settings nav",
    }),
    [SettingsSectionEnum.CHARGEBACKS]: t("Chargebacks", {
      context: "settings nav",
    }),
    [SettingsSectionEnum.CUSTOMER_ACCOUNTS]: t("Customer accounts", {
      context: "settings nav",
    }),
    [SettingsSectionEnum.MARKETING]: t("Marketing", {
      context: "settings nav",
    }),
    [SettingsSectionEnum.ORDER_TRACKING]: t("Order tracking", {
      context: "settings nav",
    }),
    [SettingsSectionEnum.PAYMENTS]: t("Payments", { context: "settings nav" }),
    [SettingsSectionEnum.RETURNS_AND_CLAIMS]: t("Returns & claims", {
      context: "settings nav",
    }),
    [SettingsSectionEnum.PRODUCTS]: t("Products", { context: "settings nav" }),
    [SettingsSectionEnum.WARRANTIES]: t("Warranties", {
      context: "settings nav",
    }),
    [SettingsSectionEnum.INVENTORY_MANAGEMENT]: t("Inventory management", {
      context: "settings nav",
    }),
    [SettingsSectionEnum.ORDER_FULFILLMENT]: t("Order management", {
      context: "settings nav",
    }),
    [SettingsSectionEnum.WAREHOUSE]: t("Warehouse operations", {
      context: "settings nav",
    }),
    [SettingsSectionEnum.DOCUMENTS_AND_PRINTING]: t("Documents & printing", {
      context: "settings nav",
    }),
    [SettingsSectionEnum.SHIPPING]: t("Shipping", { context: "settings nav" }),
    [SettingsSectionEnum.INTERNATIONAL]: t("International", {
      context: "settings nav",
    }),
    [SettingsSectionEnum.SUPPORT]: t("Support", { context: "settings nav" }),
    [SettingsSectionEnum.RECOVER]: t("Recover", { context: "settings nav" }),
    [SettingsSectionEnum.SUPPLIES]: t("Supplies", { context: "settings nav" }),
  };
}

export interface SettingsSection {
  name: SettingsSectionEnum;
  items: SettingItem[];
}

/** Landing pages sort ahead of everything else; the rest is alphabetical. */
const PINNED_FIRST_SETTING_NAMES = new Set(["General", "Home"]);

function compareSettingFriendlyNames(a: string, b: string): number {
  const pinnedRank = (name: string) =>
    PINNED_FIRST_SETTING_NAMES.has(name) ? 0 : 1;
  return pinnedRank(a) - pinnedRank(b) || a.localeCompare(b);
}

export function sortSettingItems(items: SettingItem[]): SettingItem[] {
  return [...items]
    .sort((a, b) => compareSettingFriendlyNames(a.friendlyName, b.friendlyName))
    .map((item) =>
      item.children
        ? { ...item, children: sortSettingItems(item.children) }
        : item,
    );
}

export function sortSettingsSections(
  sections: SettingsSection[],
  sectionLabels: Record<
    SettingsSectionEnum,
    string
  > = settingsSectionToFriendly,
): SettingsSection[] {
  return [...sections]
    .sort((a, b) =>
      compareSettingFriendlyNames(sectionLabels[a.name], sectionLabels[b.name]),
    )
    .map((section) => ({ ...section, items: sortSettingItems(section.items) }));
}

const MAX_VISIBLE_SETTING_SEARCH_MATCHES = 3;
const MAX_SETTING_SEARCH_TOOLTIP_HINTS = 5;
const MIN_SETTING_SEARCH_MATCH_SCORE = 1;
const MIN_PAGE_HIGHLIGHT_QUERY_LENGTH = 2;
const MIN_PREFIX_SEARCH_TOKEN_LENGTH = 3;
const LOW_SIGNAL_SEARCH_HINT_TOKENS = new Set([
  "contact",
  "customer",
  "faq",
  "form",
  "live",
  "page",
  "setting",
  "settings",
  "support",
  "widget",
]);
const EXACT_SETTING_SEARCH_HINT_TOKENS = new Set(["gmail", "outlook"]);
const SEARCH_HINT_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "for",
  "in",
  "of",
  "on",
  "the",
  "to",
]);

interface SettingSearchMatch {
  matchCount: number;
  visibleMatches: readonly SettingItemSearchMatch[];
  tooltipHints: readonly string[];
  remainingCount: number;
}

type SettingSearchEvidenceKind = "group" | "page" | "setting";

export interface SettingSearchEvidence {
  kind: SettingSearchEvidenceKind;
  label: string;
  matchedTerms: readonly string[];
}

interface SettingSearchEvidenceInput {
  groupLabel?: string;
  groupValues?: readonly (string | undefined)[];
  pageLabel?: string;
  pageValues: readonly (string | undefined)[];
  search: string;
  settingLabels?: readonly (string | undefined)[];
  settingValues: readonly (string | undefined)[];
}

export function getSettingSearchMatch({
  item,
  search,
}: {
  item: SettingItem;
  search: string;
}): SettingSearchMatch | undefined {
  const queryTokens = getSearchHintTokens(search);
  if (queryTokens.length === 0) {
    return undefined;
  }

  const searchHintMatches: SettingItemSearchMatch[] | undefined =
    item.searchHints?.map((hint) => ({ label: hint }));
  const matchCandidates: readonly SettingItemSearchMatch[] | undefined = item
    .searchMatches?.length
    ? item.searchMatches
    : searchHintMatches;
  if (!matchCandidates?.length) {
    return undefined;
  }

  const matches = matchCandidates
    .map((match, index) => ({
      index,
      match,
      score: getSearchHintScore({
        hint: [match.label, ...(match.searchTerms ?? [])].join(" "),
        queryTokens,
      }),
    }))
    .filter((match) => match.score >= MIN_SETTING_SEARCH_MATCH_SCORE)
    .sort((a, b) => b.score - a.score || a.index - b.index);

  const visibleMatches = matches
    .slice(0, MAX_VISIBLE_SETTING_SEARCH_MATCHES)
    .map((match) => match.match);
  const tooltipHints = matches
    .slice(0, MAX_SETTING_SEARCH_TOOLTIP_HINTS)
    .map((match) => match.match.label);

  if (visibleMatches.length === 0) {
    return undefined;
  }

  return {
    matchCount: matches.length,
    visibleMatches,
    tooltipHints,
    remainingCount: Math.max(matches.length - tooltipHints.length, 0),
  };
}

function filterSettingItemsBySearchEvidence<TItem extends SettingItem>({
  items,
  search,
}: {
  items: readonly TItem[];
  search: string;
}): TItem[] {
  const queryTokens = getSearchHintTokens(search);
  if (queryTokens.length === 0) {
    return [...items];
  }

  return items.filter((item) => {
    return !!getSettingSearchEvidence({
      pageLabel: item.friendlyName,
      search,
      pageValues: getSettingItemPageSearchValues(item),
      settingLabels: getSettingItemSettingSearchLabels(item),
      settingValues: getSettingItemSettingSearchValues(item),
    });
  });
}

export function getSettingSearchEvidence({
  groupLabel,
  groupValues = [],
  pageLabel,
  pageValues,
  search,
  settingLabels = [],
  settingValues,
}: SettingSearchEvidenceInput): SettingSearchEvidence | undefined {
  const queryTokens = getSearchHintTokens(search);
  if (queryTokens.length === 0) {
    return undefined;
  }

  const values = [...pageValues, ...groupValues, ...settingValues];
  if (!hasExactSettingSearchTokenMatch({ search, values })) {
    return undefined;
  }

  if (hasAllSearchHintTokenMatches({ queryTokens, values: pageValues })) {
    return {
      kind: "page",
      label:
        getSearchEvidenceTokenMatchLabel({ queryTokens, values: pageValues }) ??
        pageLabel ??
        getFirstSearchEvidenceLabel(pageValues) ??
        t("Page", { context: "settings search" }),
      matchedTerms: queryTokens,
    };
  }

  const settingScore = getSearchHintScore({
    hint: settingValues.filter(Boolean).join(" "),
    queryTokens,
  });

  if (settingScore >= MIN_SETTING_SEARCH_MATCH_SCORE) {
    return {
      kind: "setting",
      label:
        getBestSettingSearchEvidenceLabel({ queryTokens, settingLabels }) ??
        getFirstSearchEvidenceLabel(settingValues) ??
        t("Matching setting", { context: "settings search" }),
      matchedTerms: queryTokens,
    };
  }

  if (hasAllSearchHintTokenMatches({ queryTokens, values: groupValues })) {
    return {
      kind: "group",
      label:
        getSearchEvidenceTokenMatchLabel({
          queryTokens,
          values: groupValues,
        }) ??
        groupLabel ??
        getFirstSearchEvidenceLabel(groupValues) ??
        t("Matching group", { context: "settings search" }),
      matchedTerms: queryTokens,
    };
  }

  return undefined;
}

function hasExactSettingSearchTokenMatch({
  search,
  values,
}: {
  search: string;
  values: readonly (string | undefined)[];
}): boolean {
  const exactQueryTokens = getExactSettingSearchTokens(search);
  if (exactQueryTokens.length === 0) {
    return true;
  }

  const valueTokens = new Set(
    values.flatMap((value) => (value ? getSearchHintTokens(value) : [])),
  );

  return exactQueryTokens.every((token) => valueTokens.has(token));
}

export function getSettingSearchMatchHint({
  item,
  search,
}: {
  item: SettingItem;
  search: string;
}): string | undefined {
  const match = getSettingSearchMatch({ item, search });
  if (!match) {
    return undefined;
  }

  return [
    match.visibleMatches.map((visibleMatch) => visibleMatch.label).join(", "),
    match.matchCount > match.visibleMatches.length
      ? `+${match.matchCount - match.visibleMatches.length}`
      : undefined,
  ]
    .filter((part): part is string => !!part)
    .join(" ");
}

function getSearchHintScore({
  hint,
  queryTokens,
}: {
  hint: string;
  queryTokens: readonly string[];
}): number {
  const hintTokens = new Set(getSearchHintTokens(hint));

  return queryTokens.reduce((score, token) => {
    if (!hintTokens.has(token)) {
      return score;
    }

    return score + (LOW_SIGNAL_SEARCH_HINT_TOKENS.has(token) ? 0.25 : 1);
  }, 0);
}

export function getSearchHintTokens(value: string): string[] {
  return value
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .map(normalizeSearchHintToken)
    .filter((token) => token.length > 0 && !SEARCH_HINT_STOP_WORDS.has(token));
}

function normalizeSearchHintToken(token: string): string {
  if (token.length > 3 && token.endsWith("s")) {
    return token.slice(0, -1);
  }

  return token;
}

function getExactSettingSearchTokens(search: string): string[] {
  return getSearchHintTokens(search).filter((token) =>
    EXACT_SETTING_SEARCH_HINT_TOKENS.has(token),
  );
}

function getSettingItemPageSearchValues(item: SettingItem): string[] {
  const sectionName =
    "sectionName" in item && typeof item.sectionName === "string"
      ? item.sectionName
      : undefined;

  return [item.friendlyName, item.href, sectionName].filter(
    (value): value is string => !!value,
  );
}

function getSettingItemSettingSearchValues(item: SettingItem): string[] {
  const matchValues = (item.searchMatches ?? []).flatMap((match) => [
    match.href,
    match.label,
    match.settingId,
    ...(match.searchTerms ?? []),
  ]);
  if (matchValues.length > 0) {
    return matchValues.filter((value): value is string => !!value);
  }

  return [...(item.searchHints ?? [])];
}

export function getSettingItemSettingSearchLabels(item: SettingItem): string[] {
  const labels = item.searchMatches?.map((match) => match.label) ?? [];
  if (labels.length > 0) {
    return labels;
  }

  return [...(item.searchHints ?? [])];
}

function getBestSettingSearchEvidenceLabel({
  queryTokens,
  settingLabels,
}: {
  queryTokens: readonly string[];
  settingLabels: readonly (string | undefined)[];
}): string | undefined {
  return settingLabels
    .map((label, index) => ({
      index,
      label,
      score: label ? getSearchHintScore({ hint: label, queryTokens }) : 0,
    }))
    .filter(
      (result): result is { index: number; label: string; score: number } =>
        !!result.label && result.score >= MIN_SETTING_SEARCH_MATCH_SCORE,
    )
    .sort((a, b) => b.score - a.score || a.index - b.index)[0]?.label;
}

function getSearchEvidenceTokenMatchLabel({
  queryTokens,
  values,
}: {
  queryTokens: readonly string[];
  values: readonly (string | undefined)[];
}): string | undefined {
  return values
    .find(
      (value) =>
        !!value?.trim() &&
        hasAllSearchHintTokenMatches({ queryTokens, values: [value] }),
    )
    ?.trim();
}

function getFirstSearchEvidenceLabel(
  values: readonly (string | undefined)[],
): string | undefined {
  return values.find((value): value is string => !!value?.trim())?.trim();
}

function hasAllSearchHintTokenMatches({
  queryTokens,
  values,
}: {
  queryTokens: readonly string[];
  values: readonly (string | undefined)[];
}): boolean {
  const valueTokens = new Set(
    values.flatMap((value) => (value ? getSearchHintTokens(value) : [])),
  );

  return queryTokens.every((token) =>
    [...valueTokens].some((valueToken) =>
      isSearchHintTokenMatch({ queryToken: token, valueToken }),
    ),
  );
}

function isSearchHintTokenMatch({
  queryToken,
  valueToken,
}: {
  queryToken: string;
  valueToken: string;
}): boolean {
  return (
    valueToken === queryToken ||
    (queryToken.length >= MIN_PREFIX_SEARCH_TOKEN_LENGTH &&
      valueToken.startsWith(queryToken))
  );
}

export const RedoSettingsNav = memo(function RedoSettingsNav({
  sections,
  exitSettingsUrl,
  hidden = false,
}: {
  sections: SettingsSection[];
  exitSettingsUrl?: string;
  /**
   * When true, renders items invisibly for tree registration (global search).
   * Only renders when command palette is open.
   */
  hidden?: boolean;
}) {
  const { t } = useTranslation();
  const isCommandPaletteOpen = useContext(CommandPaletteOpenContext);

  const settingsNavItem = useMemo(
    () => ({
      friendlyName: t("Settings", { context: "settings nav" }),
      Icon: SettingsIcon,
      url: undefined,
    }),
    [t],
  );
  const { id: settingsNodeId, TreeContext } = useNavTreeNode(settingsNavItem);

  const sortedSections = useMemo(
    () => sortSettingsSections(sections),
    [sections],
  );

  // When hidden, only render for tree registration when command palette is open
  if (hidden) {
    if (!isCommandPaletteOpen) {
      return null;
    }
    return (
      <TreeContext.Provider value={settingsNodeId}>
        {sortedSections.map((section) => (
          <SettingsSectionComponent
            hidden
            items={section.items}
            key={section.name}
            name={section.name}
          />
        ))}
      </TreeContext.Provider>
    );
  }

  if (exitSettingsUrl === undefined) {
    throw new Error("Visible settings navigation requires an exit URL");
  }

  return (
    <TreeContext.Provider value={settingsNodeId}>
      <VisibleSettingsNav
        exitSettingsUrl={exitSettingsUrl}
        sections={sortedSections}
      />
    </TreeContext.Provider>
  );
});

/**
 * The visible settings navigation with full UI (back button, search, user profile).
 */
interface SearchableSettingItem extends SettingItem {
  sectionName: string;
}

const VisibleSettingsNav = memo(function VisibleSettingsNav({
  sections,
  exitSettingsUrl,
}: {
  sections: SettingsSection[];
  exitSettingsUrl: string;
}) {
  const { t } = useTranslation();
  const sectionNames = useMemo(() => localizedSettingsSectionNames(t), [t]);
  const [search, setSearch] = useState("");

  const flattenedItems = useMemo(
    () =>
      sections.flatMap((section) =>
        section.items.map((item) => ({
          ...item,
          sectionName: sectionNames[section.name],
        })),
      ),
    [sectionNames, sections],
  );

  const itemSearcher = useMemo(() => {
    const searcher = new Fuse<SearchableSettingItem>([], {
      keys: ["friendlyName", "sectionName", "searchTerms"],
      threshold: 0.4,
    });
    return searcher;
  }, []);

  const fuzzySearchedItems = useSearch(itemSearcher, flattenedItems, search);
  const searchedItems = useMemo(
    () =>
      filterSettingItemsBySearchEvidence({ items: fuzzySearchedItems, search }),
    [fuzzySearchedItems, search],
  );

  const lastDispatchedHighlightRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const trimmed = search.trim();
    const nextHighlight =
      trimmed.length >= MIN_PAGE_HIGHLIGHT_QUERY_LENGTH ? trimmed : "";
    if (nextHighlight === lastDispatchedHighlightRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      lastDispatchedHighlightRef.current = nextHighlight;
      dispatchTextHighlight({ highlightText: nextHighlight });
    }, 150);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search]);

  const modifiedSections = useMemo(() => {
    const searchItemsInResults = new Set(searchedItems.map((item) => item.id));

    const modifiedSections = sections.map((section) => {
      const items = section.items.filter((item) =>
        searchItemsInResults.has(item.id),
      );
      return { name: section.name, items };
    });

    const nonEmptySections = modifiedSections.filter(
      (section) => section.items.length > 0,
    );

    return nonEmptySections;
  }, [searchedItems, sections]);

  return (
    <Flex
      bgColor="primary"
      className={classNames(navigationCss.settingsNav, darkModeCss.darkMode)}
      data-arbiter="RedoSettingsNav"
      data-exclude-text-highlight="true"
      dir="column"
      grow={1}
      justify="space-between"
    >
      <Flex dir="column" gap="xl" overflow="hidden" py="3xl">
        <Flex px="sm">
          <Flex
            align="center"
            as="a"
            borderRadius="md"
            className={navigationCss.backButton}
            grow={1}
            linkProps={{ to: exitSettingsUrl }}
            px="3xl"
            py="sm"
          >
            <RedoIcon color="tertiary" Icon={RedoIconArrowLeft} size={16} />{" "}
            <Text fontSize="xs" fontWeight="regular" textColor="secondary">
              {t("Back", { context: "settings nav" })}
            </Text>
          </Flex>
        </Flex>
        <Flex dir="column" gap="xs" px="3xl">
          <RedoTextInput
            autoFocus
            IconLeading={SearchSm}
            placeholder={t("Search", { context: "settings search" })}
            setValue={setSearch}
            value={search}
          />
        </Flex>
        <Flex
          className={navigationCss.settingsNavItems}
          dir="column"
          gap="lg"
          px="xl"
        >
          {modifiedSections.map((section) => (
            <SettingsSectionComponent
              items={section.items}
              key={section.name}
              name={section.name}
              search={search}
            />
          ))}
        </Flex>
      </Flex>
      <UserProfile collapsed={false} />
    </Flex>
  );
});

export function settingItemToProps(item: SettingItem): SecondaryNavItemProps {
  const base = {
    action: { type: "link" as const, href: item.href },
    id: item.id,
    friendlyName: item.friendlyName,
  };
  if (item.UnfilledIcon && item.FilledIcon) {
    return {
      ...base,
      UnfilledIcon: item.UnfilledIcon,
      FilledIcon: item.FilledIcon,
    };
  }
  return base;
}

function settingChildMatchesPath(
  child: SettingItem,
  pathname: string,
): boolean {
  return pathname === child.href || pathname.startsWith(`${child.href}/`);
}

/** Folder row is current only when no child owns the path. */
export function isSettingFolderSelfSelected(
  item: SettingItem,
  pathname: string,
): boolean {
  if (pathname !== item.href) {
    return false;
  }
  return !item.children?.some((child) =>
    settingChildMatchesPath(child, pathname),
  );
}

/**
 * Renders a setting item, expanding into a folder of nested sub-items when the
 * item has children (e.g. Billing → Bills, Activity). The folder auto-expands
 * when the current route matches one of the children.
 */
export const SettingNavItem = memo(function SettingNavItem({
  item,
  hidden = false,
  search = "",
}: {
  item: SettingItem;
  hidden?: boolean;
  search?: string;
}) {
  const { pathname } = useLocation();
  const selfSelected = pathname === item.href;
  const searchMatch = getSettingSearchMatch({ item, search });
  const itemProps = settingItemToProps(item);
  const searchMatchTooltip = searchMatch
    ? getSettingSearchMatchTooltip(searchMatch)
    : undefined;

  if (!item.children || item.children.length === 0) {
    return (
      <RedoSecondaryNavItem
        {...itemProps}
        hidden={hidden}
        muted={!!searchMatch}
        notificationCount={searchMatch?.matchCount}
        notificationTooltip={searchMatchTooltip}
      />
    );
  }

  const childSelected = item.children.some((child) =>
    settingChildMatchesPath(child, pathname),
  );
  const anyChildSelected = selfSelected || childSelected;

  return (
    <RedoSecondaryNavItem
      {...itemProps}
      anyChildSelected={anyChildSelected}
      hidden={hidden}
      isFolder
      muted={!!searchMatch}
      notificationCount={searchMatch?.matchCount}
      notificationTooltip={searchMatchTooltip}
      selected={isSettingFolderSelfSelected(item, pathname)}
    >
      {item.children.map((child) => (
        <SettingNavItem
          hidden={hidden}
          item={child}
          key={child.id}
          search={search}
        />
      ))}
    </RedoSecondaryNavItem>
  );
});

/**
 * Renders a settings section with its items.
 * When hidden, registers with tree context for global search with proper breadcrumbs.
 */
const SettingsSectionComponent = memo(function SettingsSectionComponent({
  name,
  items,
  hidden = false,
  search = "",
}: SettingsSection & { hidden?: boolean; search?: string }) {
  const { t } = useTranslation();
  const sectionNames = useMemo(() => localizedSettingsSectionNames(t), [t]);
  // Register section as parent node for breadcrumbs (e.g., "General", "AI")
  const sectionNavItem = useMemo(
    () => ({
      friendlyName: sectionNames[name],
      Icon: undefined,
      url: undefined,
    }),
    [name, sectionNames],
  );
  const { id: sectionNodeId, TreeContext } = useNavTreeNode(sectionNavItem);

  if (hidden) {
    return (
      <TreeContext.Provider value={sectionNodeId}>
        {items.map((item) => (
          <SettingNavItem hidden item={item} key={item.id} />
        ))}
      </TreeContext.Provider>
    );
  }

  return (
    <TreeContext.Provider value={sectionNodeId}>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="regular" textColor="secondary">
          {sectionNames[name]}
        </Text>
        <Flex dir="column" gap="xs">
          {items.map((item) => (
            <SettingNavItem item={item} key={item.id} search={search} />
          ))}
        </Flex>
      </Flex>
    </TreeContext.Provider>
  );
});

function getSettingSearchMatchTooltip(searchMatch: SettingSearchMatch): {
  title: string;
  subtitle?: string;
} {
  return {
    title:
      searchMatch.matchCount === 1
        ? t("1 matching setting", { context: "settings search" })
        : t("{{count}} matching settings", {
            context: "settings search",
            count: searchMatch.matchCount,
          }),
    subtitle: [
      searchMatch.tooltipHints.join(", "),
      searchMatch.remainingCount > 0
        ? t("+{{count}} more", {
            context: "settings search",
            count: searchMatch.remainingCount,
          })
        : undefined,
    ]
      .filter((part): part is string => !!part)
      .join(" "),
  };
}
